import { Button } from '../../components/Button';
import { Checkbox } from '../../components/Checkbox';
import { Pressable } from '../../primitives/Pressable';
import { Text } from '../../primitives/Text';
import { View } from '../../primitives/View';
import { DARK_LIFTED } from '../../styles/elevation';
import { cn } from '../../utils/cn';
import { subTestID } from '../../utils/testId';
import type {
  ShareSetDialogBodyProps,
  ShareSetGroupRow,
} from './ShareSetDialogBody.types';

/**
 * The share-a-Set sheet's body — ONE surface, two shells (a DS `Dialog` on
 * web, a DS `BottomSheet` on mobile).
 *
 * It exists because sharing a Set is the moment the product has the user's
 * attention and almost never uses it: ~96% of people who share follow nobody,
 * and ~3% are in a study group. So the sheet is deliberately a BRIDGE — the
 * link is still one tap away, but the groups the sharer already has are the
 * thing in the middle of the screen, and a sharer with no groups is offered
 * one that arrives with this Set already in it.
 *
 * Anatomy (Figma 7:27013 / 7:24336):
 *   mascot · title · subtitle
 *   [✓ Share Sources] [✓ Share AI Helps]
 *   … group rows, or the create-a-group
 *   Telegram · WhatsApp · Copy Link · Share with Groups   (web)
 *   Share with Groups / Share link — full width, stacked  (native: `onShare`)
 *
 * The chips and rows sit straight on the shell's surface. Figma's inner glass
 * "Detailed Users Container" is gone: inside a sheet's content surface it was
 * a card in a card (design review 2026-09: "one container too many").
 *
 * Two things the block deliberately does NOT own:
 *   - **the close ✕ and the card.** Both belong to the shell: web's `Dialog`
 *     pins a `DialogClose` in its corner and paints the card; a native sheet
 *     has no ✕ at all (it closes by drag, backdrop or back).
 *   - **any mutation.** Every handler is the caller's. The sheet is the same
 *     on both platforms; what "share to a group" does to the sync client is
 *     the caller's business, and the analytics live with it.
 *
 * Labels arrive pre-translated.
 */
export function ShareSetDialogBody({
  labels,
  icons,
  mascot,
  groups,
  selectedGroupIds,
  onToggleGroup,
  renderGroupAvatar,
  renderScrollArea,
  showSourcesToggle,
  shareSources,
  onShareSourcesChange,
  shareAiHelps,
  onShareAiHelpsChange,
  onCreateGroup,
  onCopyLink,
  onWhatsApp,
  onTelegram,
  onShareToGroups,
  onShare,
  onRevokeLink,
  busy = false,
  className,
  testID,
}: ShareSetDialogBodyProps) {
  const hasGroups = groups.length > 0;
  // A group already carrying this Set is not a share the button can perform,
  // so it never counts toward "is there anything to submit".
  const hasSubmittable = groups.some(
    (g) => !g.alreadyShared && selectedGroupIds.includes(g.id),
  );

  const list = (
    <View className="w-full flex-col gap-2">
      {groups.map((group) => (
        <GroupRow
          key={group.id}
          group={group}
          checked={group.alreadyShared || selectedGroupIds.includes(group.id)}
          onToggle={onToggleGroup}
          avatar={renderGroupAvatar(group)}
          testID={subTestID(testID, `group-${group.id}`)}
        />
      ))}
    </View>
  );

  return (
    <View className={cn('w-full flex-col gap-5', className)} testID={testID}>
      <View className="w-full flex-col items-center gap-4">
        {/* No fixed box around it: the art sizes ITSELF, so each shell can
            pick the size its viewport affords. Web has the room for Figma's
            64; the phone sheet has to seat the group list, the share buttons
            AND the revoke link above an iPhone SE's fold, and runs smaller. */}
        {mascot}
        {/* `text-center` sits on each Text: native has no text inheritance
            through a View, so a wrapper-only rule centres on web only. */}
        <View className="w-full flex-col items-center">
          <Text
            testID={subTestID(testID, 'title')}
            className="text-center text-h1 font-bold text-title-950 native:font-mona-bold">
            {labels.title}
          </Text>
          <Text className="text-center text-b1 font-medium text-secondary-600 native:font-mona-medium">
            {labels.subtitle}
          </Text>
        </View>
      </View>

      <View className="w-full flex-col gap-4">
        <View className="flex-row flex-wrap gap-2">
          {showSourcesToggle ? (
            <ToggleChip
              label={labels.shareSources}
              testID={subTestID(testID, 'share-sources')}
              checked={shareSources}
              onToggle={() => onShareSourcesChange(!shareSources)}
            />
          ) : null}
          <ToggleChip
            label={labels.shareAiHelps}
            testID={subTestID(testID, 'share-ai-helps')}
            checked={shareAiHelps}
            onToggle={() => onShareAiHelpsChange(!shareAiHelps)}
          />
        </View>

        {hasGroups ? (
          renderScrollArea ? (
            renderScrollArea(list)
          ) : (
            list
          )
        ) : (
          <View className="w-full flex-col items-center gap-2 py-4">
            <Button
              variant="secondary"
              size="l"
              leftIcon={icons.usersRound}
              testID={subTestID(testID, 'create-group')}
              onPress={onCreateGroup}>
              {labels.createGroup}
            </Button>
            <Text className="text-center text-caption-1-medium font-medium text-secondary-400 native:font-mona-medium">
              {labels.createGroupHint}
            </Text>
          </View>
        )}
      </View>

      <View className="w-full flex-col items-center gap-2">
        {onShare ? (
          // Native: the OS share sheet is the link's ONE button. With groups,
          // "Share with Groups" leads and the link follows; without any, the
          // link is the sheet's only action (the create-a-group CTA lives in
          // the body above).
          <View className="w-full flex-col gap-2">
            {hasGroups ? (
              <Button
                variant="primary"
                size="xl"
                fullWidth
                leftIcon={icons.globe}
                loading={busy}
                disabled={!hasSubmittable}
                testID={subTestID(testID, 'share-groups')}
                onPress={onShareToGroups}>
                {labels.shareWithGroups}
              </Button>
            ) : null}
            <Button
              variant={hasGroups ? 'secondary' : 'primary'}
              size="xl"
              fullWidth
              leftIcon={icons.share ?? icons.link}
              disabled={busy}
              testID={subTestID(testID, 'share')}
              onPress={onShare}>
              {labels.share ?? labels.copyLink}
            </Button>
          </View>
        ) : (
          <View className="flex-row flex-wrap items-center justify-center gap-2">
            <Button
              variant="secondary"
              size="l"
              icon={icons.telegram}
              accessibilityLabel={labels.telegram}
              testID={subTestID(testID, 'telegram')}
              onPress={onTelegram}
            />
            <Button
              variant="secondary"
              size="l"
              icon={icons.whatsapp}
              accessibilityLabel={labels.whatsapp}
              testID={subTestID(testID, 'whatsapp')}
              onPress={onWhatsApp}
            />
            {/* The link is the sheet's OTHER job, so it keeps its label while
              there is room for it — and collapses to the icon once the group
              list has taken the width (Figma 7:24336). */}
            {hasGroups ? (
              <Button
                variant="secondary"
                size="l"
                icon={icons.link}
                accessibilityLabel={labels.copyLink}
                testID={subTestID(testID, 'copy-link')}
                onPress={onCopyLink}
              />
            ) : (
              <Button
                variant="secondary"
                size="l"
                leftIcon={icons.link}
                testID={subTestID(testID, 'copy-link')}
                onPress={onCopyLink}>
                {labels.copyLink}
              </Button>
            )}
            <Button
              variant="primary"
              size="xl"
              leftIcon={icons.globe}
              loading={busy}
              disabled={!hasGroups || !hasSubmittable}
              testID={subTestID(testID, 'share-groups')}
              onPress={onShareToGroups}>
              {labels.shareWithGroups}
            </Button>
          </View>
        )}
        {onRevokeLink && labels.revokeLink ? (
          // Not in the design. Kept because web has always had it and a link
          // you cannot take back is a worse product than an extra text button
          // — parity with the platform that has the feature, not with the frame.
          <Button
            variant="ghost"
            size="s"
            leftIcon={icons.trash}
            testID={subTestID(testID, 'revoke-link')}
            onPress={onRevokeLink}>
            {labels.revokeLink}
          </Button>
        ) : null}
      </View>
    </View>
  );
}
ShareSetDialogBody.displayName = 'ShareSetDialogBody';

/** One "Share Sources" / "Share AI Helps" chip — the whole chip is the target. */
function ToggleChip({
  label,
  checked,
  onToggle,
  testID,
}: {
  label: string;
  checked: boolean;
  onToggle: () => void;
  testID?: string;
}) {
  return (
    <Pressable
      // `flex` is load-bearing on web, not cosmetic: Pressable's web element is
      // a plain `<div>` (only `View` carries the RN flex reset), so without it
      // `flex-row` and `gap-2` do nothing and the box sits flush to its label.
      // No-op on native, where Pressable is already a flex container.
      className={`flex h-11 flex-row items-center gap-2 rounded-[14px] bg-secondary-50 ${DARK_LIFTED} py-2 pl-[10px] pr-3`}
      accessibilityRole="checkbox"
      accessibilityLabel={label}
      testID={testID}
      onPress={onToggle}
      haptic="selection">
      {/* Presentational: the chip owns the role and the press, so the box must
          not announce itself as a second control inside it. */}
      <Checkbox size="m" checked={checked} interactive={false} />
      <Text className="text-caption-1-bold font-bold text-secondary-600 native:font-mona-bold">
        {label}
      </Text>
    </Pressable>
  );
}

/** Inline dim for an already-shared row — see the `Pressable` below. */
const DIM_ROW = { style: { opacity: 0.6 } } as const;

/** One group row. The whole row toggles; the checkbox is the indicator. */
function GroupRow({
  group,
  checked,
  onToggle,
  avatar,
  testID,
}: {
  group: ShareSetGroupRow;
  checked: boolean;
  onToggle: (groupId: string) => void;
  avatar: React.ReactNode;
  testID?: string;
}) {
  return (
    <Pressable
      // `flex`: see the chip above — Pressable is a bare `<div>` on web.
      // Dark: the LIFTED step (elevation.ts), light's white pill on the glass
      // mirrored — the rows (and the chip) have no hairline, so with no fill
      // a tappable row had no edge at all.
      className={`flex w-full flex-row items-center justify-between rounded-[20px] bg-secondary-50 ${DARK_LIFTED} py-2 pl-4 pr-2`}
      // An already-shared row recedes. Inline, not a toggled `opacity-60`:
      // flipping a runtime-processed NativeWind class on a mounted row crashes
      // the interop layer (`no-runtime-class-toggle`, same idiom as ScheduleList).
      webProps={group.alreadyShared ? DIM_ROW : undefined}
      nativeProps={group.alreadyShared ? DIM_ROW : undefined}
      accessibilityRole="checkbox"
      accessibilityLabel={`${group.name}, ${group.caption}`}
      disabled={group.alreadyShared}
      testID={testID}
      onPress={() => onToggle(group.id)}
      haptic="selection">
      <View className="min-w-0 flex-1 flex-row items-center gap-2">
        {avatar}
        <View className="min-w-0 flex-1 flex-col justify-center">
          <Text
            numberOfLines={1}
            className="text-l1 font-bold text-body-600 native:font-mona-bold">
            {group.name}
          </Text>
          <Text
            numberOfLines={1}
            className="text-caption-2-medium font-medium text-secondary-400 native:font-mona-medium">
            {group.caption}
          </Text>
        </View>
      </View>
      <Checkbox
        size="l"
        variant="primary"
        checked={checked}
        disabled={group.alreadyShared}
        interactive={false}
      />
    </Pressable>
  );
}
